import { Box } from '@mui/material';
import { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';

import { useAppearance } from '~/data/appearance';
import { fetchEmployee, useEmployee } from '~/data/employee';
import { fetchEmployeeDetails } from '~/data/employeeProfile';
import { useAuthentication } from '~/data/user/useAuthentication';
import {
  DispatchReduxFetch,
  useInitializeCommonReduxData,
} from '~/hooks/initializeData';
import { TOPBAR_HEIGHT, Topbar } from '~/legacy-components/Topbar';
import {
  LibraryComponentContext,
  LibraryComponentContextProps,
} from '~/library/components/LibraryComponentContext';
import { LibraryComponentProviders } from '~/library/components/LibraryComponentProviders';
import { Providers } from '~/Providers';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';
import { AppDispatch } from '~/store';
import { mergeSxProps } from '~/utils/helpers/sxProps';

export type PortalTopBarProps = LibraryComponentContextProps;

export const PortalTopBar = (props: PortalTopBarProps) => {
  const { onUpdateLegacy } = props;

  const { userDetails, isAuthenticated } = useAuthentication();

  const {
    requiredStatus: { isInitializing },
  } = useInitializeCommonReduxData({
    required: [
      DispatchReduxFetch.ApplicationConfig,
      DispatchReduxFetch.CustomAppearance,
      DispatchReduxFetch.CompanyData,
      DispatchReduxFetch.AllEnums,
      DispatchReduxFetch.Languages,
      DispatchReduxFetch.AllEmployees,
      DispatchReduxFetch.AllOrgUnits,
    ],
  });

  const dispatch: AppDispatch = useDispatch();
  useEffect(() => {
    if (isAuthenticated && !!userDetails) {
      const employeeId = userDetails!.employeeId;
      dispatch(fetchEmployee({ empId: `${employeeId}` }));
      dispatch(fetchEmployeeDetails(`${employeeId}`));
    }
  }, [dispatch, isAuthenticated, userDetails]);

  const uEmployee = useEmployee();

  const portalIsLoading =
    !![uEmployee].find((dataObj) => dataObj.isLoading && !dataObj.hasData) ||
    isInitializing;

  if (!isAuthenticated || !userDetails) {
    return null;
  }

  if (portalIsLoading) {
    return <BrandedTopBarLoadingView />;
  }

  return (
    <LibraryComponentProviders>
      <LibraryComponentContext.Provider value={{ onUpdateLegacy }}>
        <Topbar />
      </LibraryComponentContext.Provider>
    </LibraryComponentProviders>
  );
};

const BrandedTopBarLoadingView = () => {
  const { customAppearance, isLoading, isEnabled } = useAppearance();

  if (isLoading || !isEnabled) {
    return <LoadingView />;
  }

  const backgroundColor = customAppearance?.fTopBarColors?.fBackgroundColor;
  const topBarSx = mergeSxProps(
    { height: `${TOPBAR_HEIGHT}px`, width: '100%' },
    !!backgroundColor ? { backgroundColor } : undefined,
  );

  return (
    <Box sx={topBarSx}>
      <LoadingView />
    </Box>
  );
};

export const renderPortalTopBar = (
  containerId: string,
  props: PortalTopBarProps,
) => {
  ReactDOM.render(
    <Providers>
      <PortalTopBar {...props} />
    </Providers>,
    document.getElementById(containerId),
  );
};
